import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiLink, FiSave } from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';

const LinkedInOauthSettings = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    client_id: '',
    client_secret: '',
    scopes: 'openid profile email',
    linkedin_version: '202511',
  });
  const [message, setMessage] = useState('');

  const { data: settings, isLoading } = useQuery(['linkedin-oauth-config'], () =>
    api.get('/linkedin-oauth/config').then((r) => r.data)
  );

  useEffect(() => {
    if (!settings) return;
    setForm({
      client_id: settings.client_id || '',
      client_secret: '',
      scopes: settings.scopes || 'openid profile email',
      linkedin_version: settings.linkedin_version || '202511',
    });
  }, [settings]);

  const saveMutation = useMutation((body) => api.put('/linkedin-oauth/config', body), {
    onSuccess: (res) => {
      queryClient.setQueryData(['linkedin-oauth-config'], res.data);
      setForm((f) => ({ ...f, client_secret: '' }));
      setMessage('LinkedIn API credentials saved.');
    },
    onError: (e) => setMessage(e.response?.data?.error || e.message || 'Save failed'),
  });

  const handleSave = (e) => {
    e.preventDefault();
    setMessage('');
    const body = {
      client_id: form.client_id.trim(),
      scopes: form.scopes.trim(),
      linkedin_version: form.linkedin_version.trim(),
    };
    if (form.client_secret.trim()) body.client_secret = form.client_secret.trim();
    saveMutation.mutate(body);
  };

  if (isLoading) {
    return <div className="loading">Loading LinkedIn API settings…</div>;
  }

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h2>
          <FiLink /> LinkedIn Official API
        </h2>
        <p>
          Configure your LinkedIn Developer App (Sign in with LinkedIn using OpenID Connect). Candidates
          authorize once; Analyze then loads their profile via the Official API.
        </p>
      </div>

      <form className="settings-form" onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="li-client-id">Client ID</label>
          <input
            id="li-client-id"
            type="text"
            autoComplete="off"
            value={form.client_id}
            onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
            placeholder="From LinkedIn Developer Portal → Auth"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="li-client-secret">Client Secret</label>
          <input
            id="li-client-secret"
            type="password"
            autoComplete="new-password"
            value={form.client_secret}
            onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))}
            placeholder={
              settings?.client_secret_set
                ? `Saved (${settings.client_secret_preview || '••••'}) — leave blank to keep`
                : 'From LinkedIn Developer Portal → Auth'
            }
          />
        </div>

        <div className="form-group">
          <label htmlFor="li-scopes">OAuth scopes</label>
          <input
            id="li-scopes"
            type="text"
            value={form.scopes}
            onChange={(e) => setForm((f) => ({ ...f, scopes: e.target.value }))}
          />
          <small className="form-hint">
            Default: <code>openid profile email</code> (Sign In / OIDC). If your app has Verified on
            LinkedIn Plus, you can also add:{' '}
            <code>{settings?.plus_scopes_hint || 'r_profile_basicinfo r_primary_current_experience r_most_recent_education'}</code>
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="li-version">LinkedIn-Version header (identityMe)</label>
          <input
            id="li-version"
            type="text"
            value={form.linkedin_version}
            onChange={(e) => setForm((f) => ({ ...f, linkedin_version: e.target.value }))}
          />
        </div>

        <div className="form-group">
          <label>Authorized redirect URL</label>
          <code style={{ display: 'block', wordBreak: 'break-all', padding: '8px 10px' }}>
            {settings?.redirect_uri || '(save once the API is reachable)'}
          </code>
          <small className="form-hint">
            Add this exact URL under LinkedIn Developer Portal → Auth → Authorized redirect URLs for
            your app. Also enable the product <strong>Sign in with LinkedIn using OpenID Connect</strong>.
          </small>
        </div>

        <div className="form-actions" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <LoadingButton
            type="submit"
            className="btn btn-primary"
            icon={FiSave}
            loading={saveMutation.isLoading}
            loadingLabel="Saving…"
          >
            Save LinkedIn API
          </LoadingButton>
          {settings?.configured ? (
            <span style={{ color: 'var(--success, #1a7f37)', fontSize: 14 }}>Configured</span>
          ) : (
            <span style={{ color: 'var(--warning, #9a6700)', fontSize: 14 }}>Not configured</span>
          )}
        </div>
      </form>

      {message && (
        <p className="studio-message" role="status" style={{ marginTop: 16 }}>
          {message}
        </p>
      )}

      <div className="help-section" style={{ marginTop: 24 }}>
        <h3>What Analyze can fetch</h3>
        <ul>
          <li>
            <strong>OIDC (openid profile email):</strong> name, email, profile photo via{' '}
            <code>/v2/userinfo</code>
          </li>
          <li>
            <strong>Verified on LinkedIn (+ scopes):</strong> public profile URL, current role, most
            recent education via <code>/rest/identityMe</code>
          </li>
          <li>
            LinkedIn does not expose full About / full experience history on self-serve products —
            candidates can paste those sections after connect.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default LinkedInOauthSettings;
